import api from './api';
import type { FacetBucket } from '../contexts/SearchContext';

export interface BrowseItem {
  catalogItemId: string;
  title: string | null;
  creators: string[];
  coverImageUrl: string | null;
  aliCatalogNumber: string | null;
  /**
   * First user-entered inventory number (private only). Omitted from public
   * search results by the indexer — consumers must treat null/undefined as
   * "not exposed" rather than "no inventory".
   */
  customCatalogNumber?: string | null;
  creationYear: number | null;
  creationDate: string | null;
  visibility: string;
  /** Image processing status from the pipeline (UPLOADING, PROCESSING, READY, FAILED). */
  processingStatus?: string | null;
  /** Catalog item status (READY, DESTROYED, CLEARED, DELETED, DUPLICATE). */
  status?: string | null;
  /** Retirement action that was applied (CLEAR_FOR_REUSE, MARK_DUPLICATE, etc.). */
  retirementAction?: string | null;
  /** For DUPLICATE items, the catalog number of the original. */
  duplicateOfCatalogNumber?: string | null;
  updatedAt: string | null;
  /**
   * GL#24: owner's user ID from SearchResultItem.user_id (camelCase: userId).
   * Present in the search API response already. Exposed here so union-search
   * callers can distinguish own items (routing to authed detail) from others'
   * public items (routing to public detail).
   */
  userId?: string;
}

export interface BrowseResponse {
  items: BrowseItem[];
  total: number;
  sort: string;
  /**
   * Facet aggregation buckets, keyed by facet name. Only populated when the
   * request included a `facets` param (opt-in); mirrors how SearchContext
   * reads `response.data.facets` — null/omitted both mean "not requested".
   */
  facets?: Record<string, FacetBucket[]> | null;
}

export type BrowseSortBy =
  | 'newest'
  | 'oldest'
  | 'title_asc'
  | 'title_desc'
  | 'year_asc'
  | 'year_desc'
  | 'catalog_asc'
  | 'catalog_desc'
  | 'custom_asc'
  | 'custom_desc';

export const browseCatalog = async (params: {
  q?: string;
  sortBy?: BrowseSortBy;
  size?: number;
  from?: number;
  ownerAliId?: string;
  ownerId?: string;
  catalogAliId?: string;
  ownerOnly?: boolean;
  itemIds?: string[];
  /**
   * GL#24: optional scope union — when present the backend returns the
   * union of the requested scopes (own ∪ public ∪ delegated) rather than
   * narrowing to a single catalog. The backend guard ignores catalogAliId
   * when scopes are supplied. Pass ['own','public','delegate'] for the
   * authenticated global search path.
   */
  scopes?: string[];
  /**
   * Facet filter selections — CSV-encoded exactly like SearchContext.tsx's
   * `appendArrayParam` (same `/api/search` endpoint, same param names).
   */
  workType?: string[];
  materials?: string[];
  techniques?: string[];
  creators?: string[];
  decade?: string[];
  /**
   * Opt-in facet aggregation request list. Only include this on the request
   * that should (re)compute buckets — callers should omit it on subsequent
   * pagination-only requests and reuse the previously returned buckets
   * (they are page-independent by construction).
   */
  facets?: string[];
}): Promise<BrowseResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set('size', String(params.size ?? 12));
  searchParams.set('from', String(params.from ?? 0));
  if (params.q) searchParams.set('q', params.q);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.ownerAliId) searchParams.set('ownerAliId', params.ownerAliId);
  if (params.ownerId) searchParams.set('owner_id', params.ownerId);
  if (params.catalogAliId) searchParams.set('catalogAliId', params.catalogAliId);
  if (params.ownerOnly) searchParams.set('ownerOnly', 'true');
  if (params.itemIds?.length) searchParams.set('itemIds', params.itemIds.join(','));
  if (params.scopes?.length) searchParams.set('scopes', params.scopes.join(','));
  if (params.workType?.length) searchParams.set('workType', params.workType.join(','));
  if (params.materials?.length) searchParams.set('materials', params.materials.join(','));
  if (params.techniques?.length) searchParams.set('techniques', params.techniques.join(','));
  if (params.creators?.length) searchParams.set('creators', params.creators.join(','));
  if (params.decade?.length) searchParams.set('decade', params.decade.join(','));
  if (params.facets?.length) searchParams.set('facets', params.facets.join(','));
  const response = await api.get(`/api/search?${searchParams.toString()}`);
  return response.data;
};
